import { create } from "zustand";
import { Applicant, ApplicantStatus, SortByJobApplyField } from "../types/applicant/applicant";

/* eslint-disable no-unused-vars -- Parameter names in interface type definitions are for documentation only */
export type FilterChipType = "ALL" | "DISCUSS" | "CONSIDER" | "REJECT" | "HR_IMPORTED";

export interface ApplicantState {
  applicants: Applicant[];
  loading: boolean;
  searchCandidateName?: string;
  jobId?: string;
  applyId?: string;
  status?: ApplicantStatus[];
  notExpiredJob?: boolean;
  sortBy?: SortByJobApplyField;
  sortDirection?: "asc" | "desc";
  page: number;
  total: number;
  viewType: number;
  // Station 1 AI Triage filters
  aiRecommendation?: string;
  minScore?: number;
  maxScore?: number;
  source?: string;
  isNearFit?: boolean;
  activeFilterChip: FilterChipType;
  // Bulk selection
  selectedApplicantIds: string[];
}

interface ApplicantActions {
  setLoading: (_loading: boolean) => void;
  setPage: (_page: number) => void;
  setTotal: (_total: number) => void;
  setApplicants: (_applicants: Applicant[]) => void;
  setStatus: (_status: ApplicantStatus[] | undefined) => void;
  setNotExpiredJob: (_value: boolean | undefined) => void;
  setJobId: (_jobId: string | undefined) => void;
  setApplyId: (_applyId: string | undefined) => void;
  setSearchCandidateName: (_name: string | undefined) => void;
  setSortBy: (_sortBy: SortByJobApplyField | undefined) => void;
  setSortDirection: (_direction: "asc" | "desc" | undefined) => void;
  resetFilter: () => void;
  setViewType: (_viewType: number) => void;
  addApplicant: (_applicants: Applicant[]) => void;
  removeApplicant: (_id: string) => void;
  updateApplicant: (_applicant: Applicant) => void;
  // Station 1 AI Triage filter actions
  setAiRecommendation: (_aiRecommendation: string | undefined) => void;
  setMinScore: (_minScore: number | undefined) => void;
  setMaxScore: (_maxScore: number | undefined) => void;
  setSource: (_source: string | undefined) => void;
  setIsNearFit: (_isNearFit: boolean | undefined) => void;
  setActiveFilterChip: (_chip: FilterChipType) => void;
  // Bulk selection actions
  setSelectedApplicantIds: (_ids: string[]) => void;
  toggleSelectApplicant: (_id: string) => void;
  selectAllApplicants: (_ids?: string[]) => void;
  clearSelectedApplicants: () => void;
  reset: () => void;
}
/* eslint-enable no-unused-vars */

const initialState: ApplicantState = {
  applicants: [],
  loading: false,
  page: 1,
  total: 0,
  viewType: 1,
  activeFilterChip: "ALL",
  selectedApplicantIds: [],
};

export type ApplicantsStore = ApplicantState & ApplicantActions;

export const useApplicantsStore = create<ApplicantsStore>((set) => ({
  ...initialState,
  setLoading: (loading: boolean) => set((state) => ({ ...state, loading })),
  setPage: (page: number) => set((state) => ({ ...state, page })),
  setTotal: (total: number) => set((state) => ({ ...state, total })),
  setApplicants: (applicants: Applicant[]) =>
    set((state) => ({ ...state, applicants })),
  setStatus: (status: ApplicantStatus[] | undefined) =>
    set((state) => ({ ...state, status })),
  setNotExpiredJob: (notExpiredJob: boolean | undefined) =>
    set((state) => ({ ...state, notExpiredJob })),
  setJobId: (jobId: string | undefined) =>
    set((state) => ({ ...state, jobId })),
  setApplyId: (applyId: string | undefined) =>
    set((state) => ({ ...state, applyId })),
  setSearchCandidateName: (searchCandidateName: string | undefined) =>
    set((state) => ({ ...state, searchCandidateName })),
  setSortBy: (sortBy: SortByJobApplyField | undefined) =>
    set((state) => ({ ...state, sortBy })),
  setSortDirection: (sortDirection: "asc" | "desc" | undefined) =>
    set((state) => ({ ...state, sortDirection })),
  setAiRecommendation: (aiRecommendation: string | undefined) =>
    set((state) => ({ ...state, aiRecommendation })),
  setMinScore: (minScore: number | undefined) =>
    set((state) => ({ ...state, minScore })),
  setMaxScore: (maxScore: number | undefined) =>
    set((state) => ({ ...state, maxScore })),
  setSource: (source: string | undefined) =>
    set((state) => ({ ...state, source })),
  setIsNearFit: (isNearFit: boolean | undefined) =>
    set((state) => ({ ...state, isNearFit })),
  setActiveFilterChip: (activeFilterChip: FilterChipType) =>
    set((state) => ({ ...state, activeFilterChip })),
  setSelectedApplicantIds: (selectedApplicantIds: string[]) =>
    set((state) => ({ ...state, selectedApplicantIds })),
  toggleSelectApplicant: (id: string) =>
    set((state) => ({
      ...state,
      selectedApplicantIds: state.selectedApplicantIds.includes(id)
        ? state.selectedApplicantIds.filter((item) => item !== id)
        : [...state.selectedApplicantIds, id],
    })),
  selectAllApplicants: (ids?: string[]) =>
    set((state) => ({
      ...state,
      selectedApplicantIds:
        ids ?? state.applicants.map((a) => a.id),
    })),
  clearSelectedApplicants: () =>
    set((state) => ({ ...state, selectedApplicantIds: [] })),
  resetFilter: () =>
    set((state) => ({
      ...state,
      searchCandidateName: undefined,
      jobId: undefined,
      applyId: undefined,
      status: undefined,
      sortBy: undefined,
      sortDirection: undefined,
      notExpiredJob: undefined,
      aiRecommendation: undefined,
      minScore: undefined,
      maxScore: undefined,
      source: undefined,
      isNearFit: undefined,
      activeFilterChip: "ALL",
      selectedApplicantIds: [],
      page: 1,
    })),
  setViewType: (viewType: number) => set((state) => ({ ...state, viewType })),
  addApplicant: (applicants: Applicant[]) =>
    set((state) => ({
      ...state,
      applicants: [...state.applicants, ...applicants],
    })),
  removeApplicant: (id: string) =>
    set((state) => ({
      ...state,
      applicants: state.applicants.filter((applicant) => applicant.id !== id),
      selectedApplicantIds: state.selectedApplicantIds.filter((item) => item !== id),
    })),
  updateApplicant: (updatedApplicant: Applicant) =>
    set((state) => ({
      ...state,
      applicants: state.applicants.map((applicant) =>
        applicant.id === updatedApplicant.id ? updatedApplicant : applicant
      ),
    })),
  reset: () => set(initialState),
}));
